import React from 'react';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { recruiterStore } from './RecruiterStore';
import { RecruiterEdit } from './RecruiterEdit';
import { RecruiterCreate } from './RecruiterCreate';
import { ListStore } from '../../../shared/stores/ListStore';
import { observer } from 'mobx-react';
import SettingsTableSkeleton from '../SettingsTableSkeleton';
import {
	rolesAllowed,
	USER_ROLES
} from '../../../shared/security/RolesAllowed';
import Dropdown from 'ats/components/dropdown/Dropdown';
import SettingsSubsectionHeader from '../SettingsSubsectionHeader';
import SettingsListToolbar from '../SettingsListToolbar';
import SettingsListEmptyState from '../SettingsListEmptyState';
import { getSettingsSectionLabel } from '../settingsSections';

const statusOptions = [
	{ text: 'All', value: 'all' },
	{ text: 'Active', value: 'active' },
	{ text: 'Inactive', value: 'inactive' }
];

const DEFAULT_STATUS_FILTER = 'active';

@rolesAllowed([USER_ROLES.ADMIN, USER_ROLES.MANAGER])
@observer
export class Recruiters extends React.Component {
	state = {
		data: [],
		filterText: '',
		filterStatus: DEFAULT_STATUS_FILTER,
		loading: true
	};

	constructor(props) {
		super(props);
		this.listStore = new ListStore(recruiterStore, {
			entityName: 'Recruiter',
			createModal: (
				<RecruiterCreate
					handleSuccess={() => {
						this.search();
						this.setState({ filterText: '' });
					}}
				/>
			),
			editModal: item => (
				<RecruiterEdit
					recruiter={item}
					handleSuccess={() => {
						this.search();
						this.setState({ filterText: '' });
					}}
				/>
			)
		});
	}

	componentDidMount() {
		this.search();
	}

	search() {
		this.setState({ loading: true });
		const { filterText, filterStatus } = this.state;

		recruiterStore
			.getRecruiters({
				query: filterText,
				status: filterStatus
			})
			.then(result => {
				if (result.status === 'success' && result.data) {
					this.setState({ data: result.data, loading: false });
				}
			});
	}

	hasFilters = () => {
		const { filterText, filterStatus } = this.state;
		const hasStatusFilter = !['all', DEFAULT_STATUS_FILTER].includes(
			filterStatus
		);

		return Boolean(filterText.trim()) || hasStatusFilter;
	};

	clearFilters = () => {
		this.setState(
			{ filterText: '', filterStatus: DEFAULT_STATUS_FILTER },
			() => {
				this.search();
			}
		);
	};

	getMenuItems(row) {
		const hasCandidates = row.candidatesCount > 0;

		return [
			{
				label: 'Edit',
				img: '/ats/icons/edit-icon.svg',
				onClick: () => this.listStore.handleEdit(row)
			},
			{
				label: 'Delete',
				img: '/ats/icons/trash-icon.svg',
				className: 'is-danger',
				disabled: hasCandidates,
				tooltip: hasCandidates
					? 'One or more candidates are associates with this recruiter.'
					: null,
				onClick: () =>
					this.listStore.handleDelete(row, this.onDeleteSuccess, true)
			}
		];
	}

	renderToolbarActions() {
		return (
			<SettingsListToolbar
				searchValue={this.state.filterText}
				onSearchChange={value => {
					this.setState({ filterText: value }, () => {
						this.search();
					});
				}}
				createLabel={`Create ${this.listStore.modalsConfigs.entityName}`}
				onCreate={() => this.listStore.handleCreate()}
				extra={
					<Dropdown
						value={this.state.filterStatus}
						placeholder="Status"
						displayValue={text => `Status: ${text}`}
						onChange={value => {
							this.setState({ filterStatus: value }, () => {
								this.search();
							});
						}}
						options={statusOptions}
						optionValue="value"
					/>
				}
			/>
		);
	}

	renderEmptyState() {
		return (
			<SettingsListEmptyState
				entityPlural="recruiters"
				hasFilters={this.hasFilters()}
				onClearFilters={this.clearFilters}
			/>
		);
	}

	render() {
		const { data, loading } = this.state;
		const isEmpty = !loading && data.length === 0;

		return (
			<div className="Recruiters container">
				<SettingsSubsectionHeader
					title={getSettingsSectionLabel('recruiters')}
					actions={this.renderToolbarActions()}
				/>
				<div className="columns">
					<div className="column is-12 table-wrapper">
						<table className="table is-fullwidth">
							<thead>
								<tr>
									<th>Name</th>
									<th>Email</th>
									<th className="actions-column" />
								</tr>
							</thead>
							{loading ? (
								<SettingsTableSkeleton columns={3} />
							) : !isEmpty ? (
								<tbody>
									{data.map(row => {
										return (
											<tr
												key={row.id}
												className={row.disabled ? 'disabled' : ''}>
												<td className="has-text-weight-bold">{row.name}</td>
												<td>{row.email}</td>
												<td className="actions-column">
													<HoverMenu
														id={row.id}
														position="left"
														buttonIcon={
															<img
																src="/ats/icons/three-dots-icon.svg"
																alt="More actions"
															/>
														}
														items={this.getMenuItems(row)}
													/>
												</td>
											</tr>
										);
									})}
								</tbody>
							) : null}
						</table>
						{!loading && isEmpty ? this.renderEmptyState() : null}
					</div>
				</div>
			</div>
		);
	}

	onDeleteSuccess = item => {
		this.search();
		this.setState({ filterText: '' });
	};
}
